import React from 'react';
import LegalLayout from '../../components/LegalLayout';

const DPA = () => (
  <LegalLayout title="MyBusinessAdPro Data Processing Agreement (DPA) — Template" lastUpdated="February 2026 · For execution with enterprise or data-controller customers">
    <p>This Data Processing Agreement forms part of the <a href="/legal/service-agreement">Service Agreement</a> between the Client (as <strong>Data Controller</strong>) and Krinal Ahir Ltd t/a MyBusinessAdPro (as <strong>Data Processor</strong>), and applies whenever MyBusinessAdPro processes personal data on the Client's behalf.</p>
    <hr />

    <h2>1. Definitions</h2>
    <ul>
      <li><strong>"Applicable Data Protection Law"</strong> means the UK GDPR, the Data Protection Act 2018, and (where the Client operates in the EU) the EU GDPR 2016/679.</li>
      <li><strong>"Data Controller"</strong> means the Client — the business that determines the purposes and means of processing personal data.</li>
      <li><strong>"Data Processor"</strong> means Krinal Ahir Ltd t/a MyBusinessAdPro.</li>
      <li><strong>"Personal Data"</strong> has the meaning given in the UK GDPR.</li>
      <li><strong>"Sub-processor"</strong> means any third party engaged by MyBusinessAdPro to process Personal Data on behalf of the Client (listed in Annex B).</li>
    </ul>

    <h2>2. Scope and roles</h2>
    <p>2.1 The Client is the Data Controller and MyBusinessAdPro is the Data Processor for Personal Data uploaded to or generated within the Client's MyBusinessAdPro account (e.g. customer enquiries, review responses, campaign contact lists).</p>
    <p>2.2 MyBusinessAdPro is an independent Controller for Personal Data it collects directly from visitors to mybusinessadpro.co.uk (including WhatsApp subscribers captured via its own popup).</p>

    <h2>3. MyBusinessAdPro's obligations as Processor</h2>
    <p>3.1 Process Personal Data only on the Client's documented instructions (the Service Agreement + configured dashboard settings).</p>
    <p>3.2 Ensure that personnel authorised to process Personal Data are bound by confidentiality obligations.</p>
    <p>3.3 Implement appropriate technical and organisational measures (Annex A).</p>
    <p>3.4 Assist the Client, as far as reasonably possible, to respond to data-subject rights requests (access, rectification, erasure, restriction, portability, objection).</p>
    <p>3.5 Assist the Client with notification of Personal Data breaches, data-protection impact assessments (DPIAs), and consultations with the ICO.</p>
    <p>3.6 Notify the Client of any Personal Data breach within <strong>72 hours</strong> of becoming aware.</p>
    <p>3.7 At the Client's choice, delete or return all Personal Data at the end of the Service Agreement (30-day default retention — Service Agreement §6).</p>
    <p>3.8 Make available to the Client all information necessary to demonstrate compliance and allow for and contribute to audits (including inspections) conducted by the Client or an appointed auditor, no more than once per calendar year, with 30 days' notice.</p>

    <h2>4. Sub-processors</h2>
    <p>4.1 The Client authorises MyBusinessAdPro to engage the Sub-processors listed in Annex B.</p>
    <p>4.2 MyBusinessAdPro will notify the Client of any intended changes to its Sub-processor list at least <strong>30 days in advance</strong>. The Client may object on reasonable data-protection grounds, in which case the parties will work in good faith to find a solution.</p>
    <p>4.3 MyBusinessAdPro remains fully liable to the Client for the acts and omissions of its Sub-processors.</p>

    <h2>5. International transfers</h2>
    <p>Where Personal Data is transferred outside the UK / EEA, MyBusinessAdPro will rely on an appropriate transfer mechanism — e.g. the UK International Data Transfer Addendum to the EU Standard Contractual Clauses, or an adequacy decision. The main Sub-processors' primary locations are listed in Annex B.</p>

    <h2>6. Liability</h2>
    <p>6.1 Each party's liability arising out of or related to this DPA is subject to the limitation of liability in the Service Agreement §10.</p>

    <h2>7. Term</h2>
    <p>This DPA remains in force for the duration of the Service Agreement and for any period after termination during which MyBusinessAdPro still holds Personal Data.</p>

    <hr />

    <h2>Annex A — Technical and organisational measures</h2>
    <table>
      <thead><tr><th>Area</th><th>Measure</th></tr></thead>
      <tbody>
        <tr><td>Encryption in transit</td><td>TLS 1.2+ for all traffic</td></tr>
        <tr><td>Encryption at rest</td><td>MongoDB-managed encryption for the production cluster</td></tr>
        <tr><td>Access control</td><td>Role-based access control, least-privilege service accounts, MFA on admin accounts</td></tr>
        <tr><td>Password security</td><td>bcrypt hashing, rate-limited login, 2FA available</td></tr>
        <tr><td>Card data</td><td>Tokenised by Stripe — never stored by MyBusinessAdPro</td></tr>
        <tr><td>Audit logging</td><td>Admin actions logged for 2 years</td></tr>
        <tr><td>Backups</td><td>Automated daily backups, 30-day retention</td></tr>
        <tr><td>Incident response</td><td>72-hour breach notification; named DPO / security contact</td></tr>
        <tr><td>Staff training</td><td>Annual data-protection training</td></tr>
        <tr><td>Vulnerability management</td><td>Dependency scanning in CI</td></tr>
      </tbody>
    </table>

    <h2>Annex B — Authorised Sub-processors</h2>
    <table>
      <thead><tr><th>Sub-processor</th><th>Purpose</th><th>Primary location</th></tr></thead>
      <tbody>
        <tr><td>MongoDB Atlas</td><td>Primary database</td><td>EU-West-2</td></tr>
        <tr><td>Kubernetes-based application host</td><td>Application hosting</td><td>EU</td></tr>
        <tr><td>Cloudflare</td><td>CDN + DDoS protection</td><td>Global</td></tr>
        <tr><td>Stripe</td><td>Payment processing</td><td>Ireland / US</td></tr>
        <tr><td>Resend (or SMTP provider)</td><td>Transactional email</td><td>EU / US</td></tr>
        <tr><td>Meta Platforms</td><td>Facebook / Instagram ad delivery</td><td>Ireland / US</td></tr>
        <tr><td>Google</td><td>Business Profile, Ads</td><td>Ireland / US</td></tr>
        <tr><td>Google Gemini (via Emergent Integrations)</td><td>AI text and image generation</td><td>US</td></tr>
        <tr><td>Twilio <strong>or</strong> Meta WhatsApp Cloud API (Phase 2)</td><td>WhatsApp outbound messaging</td><td>Ireland / US</td></tr>
      </tbody>
    </table>

    <h2>Annex C — Categories of data processed</h2>
    <table>
      <thead><tr><th>Category</th><th>Data subjects</th><th>Examples</th></tr></thead>
      <tbody>
        <tr><td>Account &amp; business data</td><td>MBAP users</td><td>Name, email, business name, business type, address</td></tr>
        <tr><td>Payment data</td><td>MBAP users</td><td>Tokenised card ref, Stripe customer &amp; subscription IDs</td></tr>
        <tr><td>Marketing engagement</td><td>Website visitors</td><td>Anonymous visitor ID, page views, sessions</td></tr>
        <tr><td>WhatsApp subscribers</td><td>Website visitors opting in</td><td>Mobile, name, consent timestamps, IP, UA</td></tr>
        <tr><td>Customer enquiries</td><td>End customers of the Client</td><td>Name, email, phone, enquiry text</td></tr>
        <tr><td>Review content</td><td>Google Business reviewers</td><td>Public review text, star rating</td></tr>
      </tbody>
    </table>

    <h2>Annex D — Data subject rights process</h2>
    <p>MyBusinessAdPro responds to Client-forwarded data-subject requests within 20 working days of the Client's notification. Direct requests from data subjects to MyBusinessAdPro are forwarded to the Client within 5 working days.</p>

    <hr />
    <p><strong>Signed for the Client:</strong> _______________ Date: _________<br />
    <strong>Signed for MyBusinessAdPro:</strong> _______________ Date: _________</p>
  </LegalLayout>
);
export default DPA;
